import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { locationService } from '../services/api';
import { analytics } from '../services/analytics';
import { useBooking } from '../context/BookingContext';
import PromoCodeModal from './PromoCodeModal';
import './SearchForm.css';

const SearchForm = () => {
  const navigate = useNavigate();
  const { updateSearchCriteria } = useBooking();
  
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickupLocation, setPickupLocation] = useState('');
  const [pickupDate, setPickupDate] = useState(new Date());
  const [pickupTime, setPickupTime] = useState('11:00');
  const [returnDate, setReturnDate] = useState(new Date(Date.now() + 2 * 24 * 60 * 60 * 1000));
  const [returnTime, setReturnTime] = useState('11:00');
  const [returnToDifferentLocation, setReturnToDifferentLocation] = useState(false);
  const [returnLocation, setReturnLocation] = useState('');
  const [showPromoModal, setShowPromoModal] = useState(false);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await locationService.getAll();
        const activeLocations = (response.data.data || []).filter(loc => loc.active);
        setLocations(activeLocations);
        if (activeLocations.length > 0) {
          setPickupLocation(activeLocations[0].name);
          setReturnLocation(activeLocations[0].name);
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setLoading(false);
      }
    };
    loadLocations();
  }, []);

  // Validar valores iniciales cuando se cargan
  useEffect(() => {
    if (pickupDate && returnDate && pickupTime && returnTime) {
      validateDateTime(pickupDate, pickupTime, returnDate, returnTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateTimeOptions = () => {
    const times = [];
    for (let i = 0; i < 24; i++) {
      times.push(`${i.toString().padStart(2, '0')}:00`);
      times.push(`${i.toString().padStart(2, '0')}:30`);
    }
    return times;
  };

  const [dateError, setDateError] = useState('');

  // Validar fechas y horas en tiempo real
  const validateDateTime = (newPickupDate, newPickupTime, newReturnDate, newReturnTime) => {
    const pickupDateTime = new Date(newPickupDate);
    pickupDateTime.setHours(parseInt(newPickupTime.split(':')[0]), parseInt(newPickupTime.split(':')[1]), 0, 0);
    
    const returnDateTime = new Date(newReturnDate);
    returnDateTime.setHours(parseInt(newReturnTime.split(':')[0]), parseInt(newReturnTime.split(':')[1]), 0, 0);
    
    // Calcular diferencia en horas
    const diffInMs = returnDateTime - pickupDateTime;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      setDateError('La reserva debe ser de al menos 1 día completo (24 horas)');
      return false;
    }
    
    setDateError('');
    return true;
  };

  const handlePickupDateChange = (date) => {
    setPickupDate(date);
    // Si la fecha de devolución es anterior a la nueva fecha de retiro, ajustarla
    if (returnDate < date) {
      const newReturnDate = new Date(date.getTime() + 24 * 60 * 60 * 1000);
      setReturnDate(newReturnDate);
      validateDateTime(date, pickupTime, newReturnDate, returnTime);
    } else {
      validateDateTime(date, pickupTime, returnDate, returnTime);
    }
  };

  const handlePickupTimeChange = (time) => {
    setPickupTime(time);
    validateDateTime(pickupDate, time, returnDate, returnTime);
  };

  const handleReturnDateChange = (date) => {
    setReturnDate(date);
    validateDateTime(pickupDate, pickupTime, date, returnTime);
  };

  const handleReturnTimeChange = (time) => {
    setReturnTime(time);
    validateDateTime(pickupDate, pickupTime, returnDate, time);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar antes de enviar
    if (!validateDateTime(pickupDate, pickupTime, returnDate, returnTime)) {
      return;
    }
    
    const searchData = {
      pickupLocation,
      pickupDate: pickupDate.toISOString(),
      pickupTime,
      returnDate: returnDate.toISOString(),
      returnTime,
      returnToDifferentLocation,
      returnLocation: returnToDifferentLocation ? returnLocation : pickupLocation
    };

    updateSearchCriteria(searchData);
    analytics.trackSearch(searchData);
    
    const params = new URLSearchParams(searchData).toString();
    navigate(`/buscar?${params}`);
  };

  if (loading) {
    return (
      <div className="search-form-home">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Cargando ubicaciones...
        </div>
      </div>
    );
  }

  return (
    <form className="search-form-home" onSubmit={handleSubmit}>
      <div className="form-main-row">
        <div className="form-group">
          <label>📍 Alquilar auto en</label>
          <select
            value={pickupLocation}
            onChange={(e) => setPickupLocation(e.target.value)}
            className="form-select"
            disabled={locations.length === 0}
          >
            {locations.length === 0 ? (
              <option value="">No hay ubicaciones disponibles</option>
            ) : (
              locations.map((location) => (
                <option key={location.id} value={location.name}>
                  {location.name}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="form-group">
          <label>📅 Fecha de retiro</label>
          <DatePicker
            selected={pickupDate}
            onChange={handlePickupDateChange}
            dateFormat="dd/MM/yyyy"
            minDate={new Date()}
            className="form-input"
          />
        </div>

        <div className="form-group time-group">
          <label>🕐 Hora</label>
          <select 
            value={pickupTime} 
            onChange={(e) => handlePickupTimeChange(e.target.value)}
            className="form-select"
          >
            {generateTimeOptions().map((time) => (
              <option key={time} value={time}>
                {time}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>📅 Fecha de devolución</label>
          <DatePicker
            selected={returnDate}
            onChange={handleReturnDateChange}
            dateFormat="dd/MM/yyyy"
            minDate={pickupDate}
            className="form-input"
          />
        </div>

        <div className="form-group time-group">
          <label>🕐 Hora</label>
          <select 
            value={returnTime} 
            onChange={(e) => handleReturnTimeChange(e.target.value)}
            className="form-select"
          >
            {generateTimeOptions().map((time) => (
              <option key={time} value={time}>
                {time}
              </option>
            ))}
          </select>
        </div>

        <button type="submit" className="btn-search">
          🔍 Buscar
        </button>
      </div>

      {dateError && (
        <div className="form-error-message">
          ⚠️ {dateError}
        </div>
      )}

      <div className="form-options-row">
        <label className="checkbox-inline">
          <input
            type="checkbox"
            checked={returnToDifferentLocation}
            onChange={(e) => setReturnToDifferentLocation(e.target.checked)}
          />
          <span>Devolución en otra ubicación</span>
        </label>
      </div>

      {returnToDifferentLocation && (
        <div className="return-location-row">
          <div className="form-group">
            <label>📍 Lugar de devolución</label>
            <select
              value={returnLocation}
              onChange={(e) => setReturnLocation(e.target.value)}
              className="form-select"
              disabled={locations.length === 0}
            >
              {locations.length === 0 ? (
                <option value="">No hay ubicaciones disponibles</option>
              ) : (
                locations.map((location) => (
                  <option key={location.id} value={location.name}>
                    {location.name}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
      )}

      <PromoCodeModal
        isOpen={showPromoModal}
        onClose={() => setShowPromoModal(false)}
      />
    </form>
  );
};

export default SearchForm;
